import { Component, DestroyRef, inject, Input, QueryList, ViewChildren } from '@angular/core';
import { CommonModule } from '@angular/common';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatAutocomplete, MatAutocompleteModule } from '@angular/material/autocomplete';
import { CustomFields, FieldConfig } from '@vendasta/platform-integrations';
import { combineLatest, EMPTY, map, Observable } from 'rxjs';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { IntegrationConnectionDetail } from '../../../../model/Integration-connections-detail';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Environment, EnvironmentService } from '@galaxy/core';
import { HttpClient } from '@angular/common/http';
import { AppleAutoCompleteService } from './auto-complete.service';

export interface AppleLocation {
  name: string; // 'Brand name > Location name' or 'Location name'
  brandId?: string;
  locationId: string;
  state: string;
  placeCardUrl?: string;
  partnersLocationId: string;
  partnersLocationVersion?: string;
}

export interface AppleBrand {
  name: string;
  brandId: string;
}

@Component({
  selector: 'platform-integration-auto-complete-field',
  imports: [CommonModule, GalaxyFormFieldModule, MatAutocompleteModule, ReactiveFormsModule],
  templateUrl: './auto-complete.component.html',
  styleUrl: './auto-complete.component.scss',
})
export class AutoCompleteComponent {
  @Input() theFormControl!: FormControl;
  @Input() fieldConfig!: FieldConfig;
  @Input() pageData!: { cardDetail: IntegrationConnectionDetail; namespace: string; connectionId: string };

  @ViewChildren(MatAutocomplete)
  private accountAutocompleteWidgets: QueryList<MatAutocomplete> | undefined;

  originalOptions: AppleLocation[] = [];
  autocompleteOptions: AppleLocation[] = [];
  platformIntegrationsHost: string = this.getPlatformIntegrationsHost();

  private readonly destroyRef = inject(DestroyRef);
  private readonly httpClient = inject(HttpClient);
  private readonly autocompleteService = inject(AppleAutoCompleteService);
  constructor(private environmentService: EnvironmentService) {}

  ngOnInit(): void {
    this.fetchInitialAutocompleteOptions(this.pageData.connectionId, this.pageData.cardDetail.connection?.customFields)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data) => {
        this.autocompleteOptions = [...data];
        this.originalOptions = [...data];

        this.theFormControl.setValue(
          this.autocompleteService.findLocation(
            this.pageData.cardDetail.connection?.customFields || [],
            this.autocompleteOptions,
          ),
        );
      });
    this.theFormControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((inputValue) => {
      this.filterAutocompleteOptions(inputValue);
      this.setSelectorProgramatically(inputValue);
    });
  }

  private filterAutocompleteOptions(inputValue: string | AppleLocation): void {
    if (!inputValue || typeof inputValue !== 'string') {
      this.autocompleteOptions = [...this.originalOptions];
      return;
    }

    const lowerInput = inputValue.toLowerCase();
    this.autocompleteOptions = this.originalOptions.filter((option) => option.name.toLowerCase().includes(lowerInput));
  }

  private setSelectorProgramatically(inputValue?: string | AppleLocation): void {
    if (!inputValue || typeof inputValue === 'string') {
      return;
    }
    const matOptions = this.accountAutocompleteWidgets?.get(0)?.options;
    if (matOptions) {
      matOptions.forEach((opt) => {
        if (opt.value.name === inputValue.name) {
          opt.select();
        } else {
          opt.deselect();
        }
      });
    }
  }

  private getPlatformIntegrationsHost(): string {
    const env = this.environmentService.getEnvironment();
    switch (env) {
      case Environment.LOCAL:
        // TODO: set this to prod for now since only Apple is using it. This should be changed to demo once
        //  DAT-1757 is complete.
        return 'https://platform-integrations-prod.apigateway.co';
      case Environment.DEMO:
        return 'https://platform-integrations-demo.apigateway.co';
      case Environment.PROD:
        return 'https://platform-integrations-prod.apigateway.co';
      default:
        return 'https://platform-integrations-demo.apigateway.co';
    }
  }

  fetchInitialAutocompleteOptions(
    connectionId: string | undefined,
    customFields: CustomFields[] | undefined,
  ): Observable<AppleLocation[]> {
    if (!connectionId || !customFields) {
      return EMPTY;
    }
    const companyId = customFields.find((f) => f.label === 'companyID');
    if (!companyId) {
      return EMPTY;
    }

    return combineLatest([
      this.getAppleLocationData(this.pageData.connectionId, companyId.value),
      this.getAppleBrandData(this.pageData.connectionId, companyId.value),
    ]).pipe(
      map(([locations, businesses]) => {
        return locations.map((loc) => {
          const business = businesses.find((b) => b.brandId === loc.brandId);
          if (!business || !business.name || business.name === '') {
            return {
              name: loc.name,
              brandId: '',
              locationId: loc.locationId,
              state: loc.state,
              placeCardUrl: loc.placeCardUrl,
              partnersLocationId: loc.partnersLocationId,
              partnersLocationVersion: loc.partnersLocationVersion,
            };
          }
          return {
            name: `${business.name} > ${loc.name}`,
            brandId: loc.brandId,
            locationId: loc.locationId,
            state: loc.state,
            placeCardUrl: loc.placeCardUrl,
            partnersLocationId: loc.partnersLocationId,
            partnersLocationVersion: loc.partnersLocationVersion,
          };
        });
      }),
    );
  }

  getAppleLocationData(connectionId: string, companyId: string): Observable<AppleLocation[]> {
    interface InternalNickname {
      name: string;
      locale: string;
    }

    interface DisplayName {
      name: string;
      locale: string;
      primary: boolean;
    }

    interface LocationDetails {
      brandId: string;
      internalNicknames: InternalNickname[];
      displayNames: DisplayName[];
      partnersLocationId: string;
      partnersLocationVersion?: string;
    }

    interface DataItem {
      id: string;
      state: string;
      placeCardUrl?: string;
      locationDetails: LocationDetails;
    }

    interface LocationsResponse {
      data: DataItem[];
      total: number;
      pagination?: {
        cursors?: {
          after?: string;
        };
        next?: string;
      };
    }
    const url =
      this.platformIntegrationsHost +
      '/passthrough/' +
      connectionId +
      '/api/v3/companies/' +
      companyId +
      '/locations?ql=state==PUBLISHED,state==PROCESSING,state==SUBMITTED,state==REJECTED';

    return this.httpClient
      .get<LocationsResponse>(url, {
        withCredentials: true,
      })
      .pipe(
        map((response: LocationsResponse): AppleLocation[] => {
          return response.data.map((item) => {
            const displayNames = item.locationDetails?.displayNames || [];
            const primaryDisplayName = displayNames.find((name) => name.primary)?.name;

            const internalNicknames = item.locationDetails?.internalNicknames || [];
            const firstInternalNickname = internalNicknames.length > 0 ? internalNicknames[0].name : '';

            // Use the primary display name if it exists, otherwise use the first internal nickname
            // TODO: we might want to use the nickname first, then fallback to display names. Alternatively we could
            //  completely ignore nicknames since we don't syndicate them.
            return {
              name: primaryDisplayName || firstInternalNickname || '',
              brandId: item.locationDetails?.brandId || '',
              locationId: item.id,
              state: item.state,
              placeCardUrl: item.placeCardUrl,
              partnersLocationId: item.locationDetails?.partnersLocationId || '',
              partnersLocationVersion: item.locationDetails?.partnersLocationVersion || '',
            };
          });
        }),
      );
  }

  getAppleBrandData(connectionId: string, companyId: string): Observable<AppleBrand[]> {
    interface DisplayName {
      name: string;
      locale: string;
      primary: boolean;
    }
    interface BrandDetails {
      displayNames: DisplayName[];
    }
    interface DataItem {
      id: string;
      brandDetails: BrandDetails;
    }
    interface BrandResponse {
      data: DataItem[];
      total: number;
      pagination?: {
        cursors?: {
          after?: string;
        };
        next?: string;
      };
    }
    const url =
      this.platformIntegrationsHost +
      '/passthrough/' +
      connectionId +
      '/api/v3/companies/' +
      companyId +
      '/brands?ql=state==PUBLISHED';
    return this.httpClient
      .get<BrandResponse>(url, {
        withCredentials: true,
      })
      .pipe(
        map((response) => {
          const brands = response.data.map((item) => {
            const displayNames = item.brandDetails?.displayNames || [];
            const primaryDisplayName = displayNames.find((name) => name.primary)?.name;
            if (!primaryDisplayName) {
              return {
                name: displayNames?.[0]?.name || '',
                brandId: item.id,
              };
            }
            return {
              name: primaryDisplayName,
              brandId: item.id,
            };
          });
          return brands;
        }),
      );
  }

  autocompleteDisplayFn(option: AppleLocation): string {
    return option?.name || '';
  }
}
